import React, {
  useEffect,
  useRef,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import "../../../styles/components/Slices/HeroTurnerCarousel.scss";
import SlicesVideoComponent from "../SlicesVideoComponent";

import { buildPrismicImgixSrc } from "../../../utilities/prismicImage";
import PlayIcon from "../../../assets/icons/PlayIcon";
import PauseIcon from "../../../assets/icons/PauseIcon";
import useWindowWidth from "../../../hooks/useBetterMediaQuery";

const carouselReducer = (state, action) => {
  switch (action.type) {
    case "SET_CURRENT_SLIDE":
      return { ...state, currentSlide: action.payload, progress: 0 };
    case "SET_PLAYING":
      return { ...state, isPlaying: action.payload };
    case "SET_PROGRESS":
      return { ...state, progress: action.payload };
    case "SET_USER_INTERACTED":
      return { ...state, hasUserInteracted: action.payload };
    case "SET_SWIPE_STATE":
      return {
        ...state,
        swipeOffset: action.payload.offset,
        isSwipping: action.payload.isSwipping,
      };
    case "SET_GRADIENTS":
      return {
        ...state,
        showLeftGradient: action.payload.left,
        showRightGradient: action.payload.right,
      };
    case "TOGGLE_PLAYING":
      return { ...state, isPlaying: !state.isPlaying, hasUserInteracted: true };
    case "RESET_SWIPE":
      return { ...state, swipeOffset: 0, isSwipping: false };
    case "TICK_PROGRESS":
      const newProgress = state.progress + action.payload.progressIncrement;
      if (newProgress >= 100) {
        const nextIndex = (state.currentSlide + 1) % action.payload.totalSlides;
        return { ...state, currentSlide: nextIndex, progress: 0 };
      }
      return { ...state, progress: newProgress };
    default:
      return state;
  }
};

const initialState = {
  currentSlide: 0,
  isPlaying: true,
  progress: 0,
  hasUserInteracted: false,
  swipeOffset: 0,
  isSwipping: false,
  showLeftGradient: false,
  showRightGradient: false,
};

const HeroTurnerSlider = ({ carouselData }) => {
  const [state, dispatch] = useReducer(carouselReducer, initialState);

  const intervalRef = useRef(null);
  const carouselRef = useRef(null);
  const tabContainerRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchMoveRef = useRef(null);
  const isDraggingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const wheelTimeoutRef = useRef(null);
  const wheelDeltaRef = useRef(0);
  const wheelTriggeredRef = useRef(false);

  const isMdOrLarger = useWindowWidth(768);

  const { validSlides, tabs } = useMemo(() => {
    const validSlides =
      carouselData?.filter(
        (card) =>
          card?.cell_image?.url ||
          card?.mobile_cell_image?.url ||
          card?.cell_video?.url,
      ) || [];

    const tabs = validSlides.map(
      (card) => card?.tab_title?.text || card?.cell_title?.text || "Slide",
    );

    return { validSlides, tabs };
  }, [carouselData]);

  const totalSlides = validSlides.length;

  const goToSlide = useCallback(
    (index, userTriggered = false) => {
      if (index < 0 || index >= totalSlides) return;

      dispatch({ type: "SET_CURRENT_SLIDE", payload: index });

      if (userTriggered) {
        dispatch({ type: "SET_USER_INTERACTED", payload: true });
      }
    },
    [totalSlides],
  );

  const nextSlide = useCallback(() => {
    const nextIndex = (state.currentSlide + 1) % totalSlides;
    goToSlide(nextIndex);
  }, [state.currentSlide, totalSlides, goToSlide]);

  const prevSlide = useCallback(() => {
    const prevIndex =
      state.currentSlide === 0 ? totalSlides - 1 : state.currentSlide - 1;
    goToSlide(prevIndex);
  }, [state.currentSlide, totalSlides, goToSlide]);

  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    const duration = state.hasUserInteracted ? 4000 : 6000;
    const progressIncrement = 100 / (duration / 50);

    intervalRef.current = setInterval(() => {
      dispatch({
        type: "TICK_PROGRESS",
        payload: { progressIncrement, totalSlides },
      });
    }, 50);
  }, [state.hasUserInteracted, totalSlides]);

  const stopAutoPlay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    dispatch({ type: "TOGGLE_PLAYING" });
    // Restart autoplay with new playing state
    if (!state.isPlaying) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }
  }, [state.isPlaying, startAutoPlay, stopAutoPlay]);

  // Enhanced touch handlers for swipe gestures
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    touchMoveRef.current = { x: touch.clientX, y: touch.clientY };
    isDraggingRef.current = false;
    dispatch({
      type: "SET_SWIPE_STATE",
      payload: { offset: 0, isSwipping: false },
    });
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Only handle horizontal swipes (prevent interfering with vertical scroll)
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
      isDraggingRef.current = true;

      e.preventDefault();

      // Limit swipe distance for visual feedback
      const maxSwipe = window.innerWidth * 0.3; // 30% of screen width
      const limitedDelta = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
      dispatch({
        type: "SET_SWIPE_STATE",
        payload: { offset: limitedDelta, isSwipping: true },
      });
    }

    touchMoveRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (!touchStartRef.current || !isDraggingRef.current) {
        dispatch({ type: "RESET_SWIPE" });
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaTime = Date.now() - touchStartRef.current.time;

      const velocity = Math.abs(deltaX) / deltaTime;

      const isValidSwipe = Math.abs(deltaX) > 50 || velocity > 0.3;

      if (isValidSwipe) {
        if (deltaX > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
        dispatch({ type: "SET_USER_INTERACTED", payload: true });
      }

      dispatch({ type: "RESET_SWIPE" });
      touchStartRef.current = null;
      touchMoveRef.current = null;
      isDraggingRef.current = false;
    },
    [nextSlide, prevSlide],
  );

  const handleWheel = useCallback(
    (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

      e.preventDefault();

      wheelDeltaRef.current += e.deltaX;

      const threshold = 50;

      if (
        !wheelTriggeredRef.current &&
        Math.abs(wheelDeltaRef.current) >= threshold
      ) {
        if (wheelDeltaRef.current > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
        dispatch({ type: "SET_USER_INTERACTED", payload: true });

        wheelTriggeredRef.current = true;
      }

      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }

      wheelTimeoutRef.current = setTimeout(() => {
        wheelDeltaRef.current = 0;
        wheelTriggeredRef.current = false;
      }, 30);
    },
    [nextSlide, prevSlide],
  );

  const updateGradientVisibility = useCallback(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const threshold = 5; // Small threshold to account for rounding

    const left = scrollLeft > threshold;
    const right = scrollLeft < scrollWidth - clientWidth - threshold;

    dispatch({ type: "SET_GRADIENTS", payload: { left, right } });
  }, []);

  const handleScroll = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      updateGradientVisibility();
    }, 10);
  }, [updateGradientVisibility]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Allow keyboard navigation when carousel is in viewport or has focus
      const carouselElement = carouselRef.current;
      if (!carouselElement) return;

      const isCarouselFocused = carouselElement.contains(
        document.activeElement,
      );
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.tagName === "SELECT" ||
          activeElement.isContentEditable);

      const enableKeyNav = isCarouselFocused || !isInputFocused;

      if (!enableKeyNav) return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          prevSlide();
          setHasUserInteracted(true);
          break;
        case "ArrowRight":
          e.preventDefault();
          nextSlide();
          setHasUserInteracted(true);
          break;
        case " ":
          if (!isInputFocused) {
            e.preventDefault();
            togglePlayPause();
          }
          break;
        case "Home":
          e.preventDefault();
          goToSlide(0, true);
          break;
        case "End":
          e.preventDefault();
          goToSlide(totalSlides - 1, true);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [prevSlide, nextSlide, togglePlayPause, goToSlide, totalSlides]);

  useEffect(() => {
    if (state.isPlaying && totalSlides > 1) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }

    return stopAutoPlay;
  }, [state.isPlaying, startAutoPlay, stopAutoPlay, totalSlides]);

  useEffect(() => {
    if (!validSlides.length) return;

    const preloadedLinks = new Map();

    const preloadImage = (index, priority) => {
      const slide = validSlides[index];
      if (!slide) return;

      // Choose appropriate image based on viewport
      const imageData =
        !isMdOrLarger && slide?.mobile_cell_image?.url
          ? slide.mobile_cell_image
          : slide?.cell_image;

      if (!imageData?.url) return;

      // Check if already preloaded
      if (preloadedLinks.has(imageData.url)) return;

      const { src, srcSet, sizes } = buildPrismicImgixSrc(imageData.url, {
        maxWidth: 1280,
        quality: priority === 0 ? 95 : 75, // Higher quality for current slide
        sizes: "(min-width: 1024px) 1152px, (min-width: 640px) 640px, 100vw",
      });

      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = src;
      link.fetchpriority = priority === 0 ? "high" : "low";
      if (srcSet) link.setAttribute("imagesrcset", srcSet);
      if (sizes) link.setAttribute("imagesizes", sizes);

      document.head.appendChild(link);
      preloadedLinks.set(imageData.url, link);
    };

    // Preload current and next image
    preloadImage(state.currentSlide, 0);
    preloadImage((state.currentSlide + 1) % totalSlides, 1);

    return () => {
      // Clean up preloaded links
      preloadedLinks.forEach((link) => {
        try {
          if (document.head.contains(link)) {
            document.head.removeChild(link);
          }
        } catch (e) {
          // Ignore if already removed
        }
      });
      preloadedLinks.clear();
    };
  }, [state.currentSlide, validSlides, totalSlides, isMdOrLarger]);

  // Auto-scroll active button into view
  useEffect(() => {
    if (tabContainerRef.current) {
      const container = tabContainerRef.current;
      const activeButton = container.querySelector(
        `button[aria-current="true"]`,
      );

      if (activeButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // Calculate if button is outside visible area
        const isOutsideLeft = buttonRect.left < containerRect.left;
        const isOutsideRight = buttonRect.right > containerRect.right;

        if (isOutsideLeft || isOutsideRight) {
          // Scroll to center the active button
          const buttonOffsetLeft = activeButton.offsetLeft;
          const containerWidth = container.clientWidth;
          const buttonWidth = activeButton.clientWidth;

          const targetScrollLeft =
            buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: "smooth",
          });
        }
      }
    }
  }, [state.currentSlide]);

  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    // Initial update
    updateGradientVisibility();

    // Optimized resize handler with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateGradientVisibility();
      }, 150); // Debounce resize events
    };

    // Intersection observer for when container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateGradientVisibility();
    });

    resizeObserver.observe(container);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [updateGradientVisibility, tabs.length]);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const progressBarStyle = useMemo(
    () => ({
      background: `conic-gradient(from 0deg, #190023 0deg, #190023 ${state.progress * 3.6}deg, transparent ${state.progress * 3.6}deg, transparent 360deg)`,
      mask: "linear-gradient(white, white) content-box, linear-gradient(white, white)",
      maskComposite: "xor",
      WebkitMask:
        "linear-gradient(white, white) content-box, linear-gradient(white, white)",
      WebkitMaskComposite: "xor",
      padding: "1px",
    }),
    [state.progress],
  );

  const carouselTrackStyle = useMemo(
    () => ({
      transform: `translateX(calc(-${state.currentSlide * 100}% + ${state.swipeOffset}px))`,
      transition:
        state.isSwipping || prefersReducedMotion
          ? "none"
          : "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    }),
    [
      state.currentSlide,
      state.swipeOffset,
      state.isSwipping,
      prefersReducedMotion,
    ],
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
      stopAutoPlay();
    };
  }, [stopAutoPlay]);

  if (!validSlides.length) return null;

  return (
    <div
      ref={carouselRef}
      className="hero-turner-carousel-bg flex flex-col-reverse sm:flex-col overflow-visible lg:flex-col"
      role="region"
      aria-label="Interactive image carousel"
    >
      {/* Controls and Tabs */}
      <div className="flex flex-col lg:flex-row w-full">
        <div className="flex items-center gap-2 my-2 w-full md:max-w-fit md:mx-auto">
          {/* Scrollable Tab Container with Gradients */}
          <div className="relative flex-1 min-w-0">
            {/* Left Gradient */}
            {state.showLeftGradient && (
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#EDEEF0] to-transparent z-10 pointer-events-none" />
            )}

            {/* Right Gradient */}
            {state.showRightGradient && (
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#EDEEF0] to-transparent z-10 pointer-events-none" />
            )}

            <div
              ref={tabContainerRef}
              className="overflow-x-auto scrollbar-none flex gap-2"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              onScroll={handleScroll}
            >
              <div className="flex gap-2 px-2">
                {tabs.map((title, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index, true)}
                    className={`flex-shrink-0 whitespace-nowrap px-3 py-3 sm:px-3 sm:py-1.5 text-xs uppercase tracking-wide font-mono font-medium rounded-[4px] border transition-colors duration-200
                       ${
                         index === state.currentSlide
                           ? "bg-[#190023] text-gray-100 border-[#190023]"
                           : "bg-gray-1500 text-gray-600 border-gray-1400 hover:bg-[#F3F2F9] hover:text-[#1D0642]"
                       }`}
                    aria-label={`Go to slide ${index + 1}: ${title}`}
                    aria-current={
                      index === state.currentSlide ? "true" : "false"
                    }
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Fixed Play/Pause Button */}
          <div className="flex-shrink-0">
            <button
              aria-label={state.isPlaying ? "Pause carousel" : "Play carousel"}
              className={`relative whitespace-nowrap justify-center px-3 py-3 sm:px-3 sm:py-1.5 ml-3 md:ml-4 text-xs uppercase tracking-wide font-mono font-medium rounded-[4px] border transition-colors duration-200 bg-gray-1500 text-gray-600 border-gray-1400 hover:bg-[#F3F2F9] hover:text-[#1D0642] overflow-hidden flex items-center gap-1 sm:w-[76px]`}
              onClick={togglePlayPause}
              title={state.isPlaying ? "Pause carousel" : "Play carousel"}
            >
              {state.isPlaying && (
                <div
                  className="absolute inset-0 rounded-[4px] pointer-events-none"
                  style={progressBarStyle}
                />
              )}
              <span className="flex-shrink-0">
                {state.isPlaying ? <PauseIcon /> : <PlayIcon />}
              </span>
              <span className="hidden sm:inline">
                {state.isPlaying ? "PAUSE" : "PLAY"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="relative w-full overflow-hidden rounded-md">
        <div
          className="carousel-track flex"
          style={carouselTrackStyle}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          {validSlides.map((slide, index) => (
            <div
              key={index}
              className="carousel-slide flex-shrink-0 w-full"
              aria-hidden={index !== state.currentSlide}
            >
              {slide?.cell_video?.url ? (
                <SlicesVideoComponent
                  loop
                  autoPlay={index === state.currentSlide}
                  muted
                  className="w-full h-auto mx-auto rounded-md lg:max-w-6xl lg:px-4"
                  src={slide.cell_video.url}
                />
              ) : (
                (() => {
                  // Choose appropriate image based on viewport
                  const hasMobileImage = !!slide?.mobile_cell_image?.url;
                  const shouldUseMobileImage = !isMdOrLarger && hasMobileImage;

                  // TEMPORARY: Test with desktop image to verify responsive logic
                  const imageData = shouldUseMobileImage
                    ? slide.mobile_cell_image
                    : slide?.cell_image;

                  const url = imageData?.url;
                  const dims = imageData?.dimensions || {};
                  const alt = imageData?.alt || `Slide ${index + 1}`;

                  if (!url) return null;

                  const { src, srcSet, sizes } = buildPrismicImgixSrc(url, {
                    maxWidth: 1280,
                    quality: index === state.currentSlide ? 95 : 75,
                    sizes:
                      "(min-width: 1024px) 1152px, (min-width: 640px) 640px, 100vw",
                  });

                  return (
                    <div className="relative w-full h-auto mx-auto rounded-md lg:max-w-6xl lg:px-0">
                      <img
                        src={src}
                        srcSet={srcSet}
                        sizes={sizes}
                        alt={alt}
                        loading={
                          index === state.currentSlide ? "eager" : "lazy"
                        }
                        decoding="async"
                        fetchpriority={
                          index === state.currentSlide ? "high" : "low"
                        }
                        width={dims.width}
                        height={dims.height}
                        className="w-full h-auto rounded-md"
                      />
                    </div>
                  );
                })()
              )}
            </div>
          ))}
        </div>

        {/* Screen reader announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Showing slide {state.currentSlide + 1} of {totalSlides}:{" "}
          {tabs[state.currentSlide]}
        </div>
      </div>
    </div>
  );
};

export default HeroTurnerSlider;
